import { useState } from "react";
import { Link } from "react-router";
import {
  Alert,
  Card,
  CardHeader,
  LoadingBlock,
  PageHeader,
  Select,
  Stat,
  StatGroup,
  buttonStyles,
} from "../../components/ui";
import { TrendChart } from "../../components/TrendChart";
import { PROGRAMME_SERIES } from "../../lib/insights";
import { formatCount, formatMoney } from "../../lib/money";
import { useProgrammeStats, useProgrammeTrend } from "../../lib/queries";
import { useTranslation } from "react-i18next";

/**
 * How the programme is doing, and the one thing on it with a clock.
 *
 * The totals answer "is this working"; the trend answers "is it working
 * *better*", which is the question a single number cannot. A queue with
 * anything in it is somebody waiting to be paid, so it is called out here and
 * linked rather than buried a tab away.
 */
export function AdminOverviewPage() {
  const { t } = useTranslation("admin");
  const stats = useProgrammeStats();

  return (
    <>
      <PageHeader
        title={t("overview.title")}
        description={t("overview.description")}
        action={
          stats.data && stats.data.queuedPayouts > 0 ? (
            /*
             * A link rather than a count, and it is the only accent-filled
             * control on the screen. Everything else here is a figure to read;
             * this is the one thing that is somebody waiting.
             */
            <Link className={buttonStyles("primary", "md")} to="/admin/payouts">
              Review {formatCount(stats.data.queuedPayouts)} payout
              {stats.data.queuedPayouts === 1 ? "" : "s"}
            </Link>
          ) : undefined
        }
      />

      {stats.error ? (
        <Alert>{stats.error.message}</Alert>
      ) : stats.isPending ? (
        <LoadingBlock />
      ) : (
        <>
          <StatGroup>
            <Stat
              boxed={false}
              size="lg"
              label={t("overview.awaiting")}
              value={formatMoney(stats.data.queuedMicros)}
              hint={t("overview.payouts", { count: stats.data.queuedPayouts })}
              tone={stats.data.queuedPayouts === 0 ? "muted" : "default"}
            />
            <Stat
              boxed={false}
              size="lg"
              label={t("overview.held")}
              value={formatMoney(stats.data.heldMicros)}
              hint={t("overview.heldHint")}
              tone={stats.data.heldMicros === 0 ? "muted" : "default"}
            />
            <Stat
              boxed={false}
              size="lg"
              label={t("overview.cleared")}
              value={formatMoney(stats.data.payableMicros)}
              hint={t("overview.clearedHint")}
              tone={stats.data.payableMicros === 0 ? "muted" : "default"}
            />
            <Stat
              boxed={false}
              size="lg"
              label={t("overview.paidOut")}
              value={formatMoney(stats.data.paidMicros)}
              hint={t("overview.paidOutHint")}
              tone={stats.data.paidMicros === 0 ? "muted" : "default"}
            />
          </StatGroup>

          {/*
           * Reversed is on its own rather than in the row above, for the reason
           * the affiliate's own overview gives: it is not a stage money passes
           * through, and a fifth column would imply it sums with the other
           * four. Shown at all because it is the programme's loss line — money
           * earned, paid, and then taken back by a refund nobody could recall.
           */}
          {stats.data.reversedMicros > 0 && (
            <p className="-mt-3 text-[12px] text-fg-subtle">
              {t("overview.reversed", { amount: formatMoney(stats.data.reversedMicros) })}
            </p>
          )}

          <div className="grid grid-cols-2 divide-x divide-line overflow-hidden rounded-lg border border-line bg-surface shadow-xs sm:grid-cols-4">
            <Stat boxed={false} label={t("overview.affiliates")} value={formatCount(stats.data.affiliates)} hint={t("overview.active", { count: formatCount(stats.data.activeAffiliates) })} />
            <Stat boxed={false} label={t("overview.clicks")} value={formatCount(stats.data.clicks)} />
            <Stat boxed={false} label={t("overview.signups")} value={formatCount(stats.data.referrals)} />
            <Stat boxed={false} label={t("overview.converted")} value={formatCount(stats.data.conversions)} />
          </div>
        </>
      )}

      <Trend />
    </>
  );
}

const WINDOWS = [30, 60, 90, 180, 365];

/**
 * The programme's shape over time.
 *
 * The window is a control rather than a fixed 30 days, because the two things
 * this chart is read for want different lengths: "did last week's campaign
 * land" is a month, and "is this growing" is a year. The API caps it at 365 and
 * refuses anything under a week, so every option here is one it accepts.
 */
function Trend() {
  const { t } = useTranslation("admin");
  const [days, setDays] = useState(30);
  const trend = useProgrammeTrend(days);

  return (
    <Card>
      <CardHeader
        title={t("overview.overTime")}
        action={
          <Select
            value={days}
            onChange={(event) => setDays(Number(event.target.value))}
            className="h-8 w-36"
            aria-label={t("overview.window")}
          >
            {WINDOWS.map((option) => (
              <option key={option} value={option}>
                {t("overview.lastDays", { count: option })}
              </option>
            ))}
          </Select>
        }
      />
      {trend.isPending ? (
        <LoadingBlock />
      ) : trend.error ? (
        <div className="px-4 py-4">
          <Alert>{trend.error.message}</Alert>
        </div>
      ) : (
        <TrendChart points={trend.data} series={PROGRAMME_SERIES} />
      )}
    </Card>
  );
}